/**
 * English: Build D3 graph station list (stations + per-station devices) from inventory V2.
 * Thai: สร้างรายการสถานีสำหรับกราฟ D3 จาก inventory V2
 */
import { useState, useEffect } from 'react';
import { useApi } from './useApi';
import { ENDPOINTS } from '../config/api';
import { Station as AppStation, DeviceInfo } from '../types';
import { colorForStationId, type Station as GraphStation } from '../utils/stationGraphData';

export function useGraphStations(stations: AppStation[], stationsLoading: boolean) {
  const { get } = useApi();
  const [graphStations, setGraphStations] = useState<GraphStation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (stationsLoading) return;
    if (stations.length === 0) {
      setGraphStations([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const withDevices = await Promise.all(
          stations.map(async (s) => {
            const data = (await get(ENDPOINTS.devicesList, { station_id: s.id })) as {
              devices?: DeviceInfo[];
            };
            const devices = (data.devices || []).map((d) => ({
              type: d.device_type,
              id: d.device_id,
              displayName: d.display_name ?? null,
              deviceDbId: d.device_db_id ?? null,
            }));
            const orgRaw = (s as AppStation & { org_name?: string }).org_name;
            return {
              id: s.id,
              name: s.name || s.id,
              orgName: orgRaw != null ? String(orgRaw) : undefined,
              color: colorForStationId(s.id),
              devices,
            } satisfies GraphStation;
          }),
        );
        if (!cancelled) {
          setGraphStations(withDevices);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load devices');
          setGraphStations([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stations, stationsLoading, get]);

  return {
    graphStations,
    loading: stationsLoading || loading,
    error,
  };
}
